import { useLocation, useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    ExclamationTriangleIcon,
    PaperAirplaneIcon,
    XCircleIcon,
    MegaphoneIcon,
    CheckCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import {
    buildApprovalSubmissionFeedback,
    getApprovalQueuePath,
} from "@/features/approvals/approval-utils";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import {
    getAnnouncementEmailCount,
    getAnnouncementFailedCount,
    getAnnouncementPushCount,
    getAnnouncementType,
    normalizeAnnouncementId,
} from "../announcement-utils";
import {
    getAnnouncementDetails,
    sendAnnouncement,
    cancelAnnouncement,
} from "../api/messagingApi";

// ============================================================================
// Helpers
// ============================================================================

const fmtDate = (d: string | null | undefined) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-NG", { dateStyle: "long", timeStyle: "short" });
};

const fmtNumber = (n: number | null | undefined) =>
    n == null ? "—" : new Intl.NumberFormat("en-NG").format(n);

const fmtLabel = (value: string | null | undefined) => {
    if (!value) return "—";
    return value
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const statusColors: Record<string, string> = {
    DRAFT: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
    SCHEDULED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    SENT: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    CANCELLED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
};

// ============================================================================
// Component
// ============================================================================

export const AnnouncementDetailsRoute = () => {
    const { announcementId } = useParams<{ announcementId: string }>();
    const normalizedAnnouncementId = normalizeAnnouncementId(announcementId);
    const location = useLocation();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const locationFeedback = (location.state as
        | { feedback?: { type: "success" | "error"; text: string } }
        | undefined)?.feedback;

    const {
        data,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["announcement-details", normalizedAnnouncementId],
        queryFn: () => getAnnouncementDetails(normalizedAnnouncementId!),
        enabled: !!normalizedAnnouncementId,
    });

    const ann = data?.data;

    // ── Mutations ──
    const sendMut = useMutation({
        mutationFn: () => sendAnnouncement(normalizedAnnouncementId!),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["announcement-details", normalizedAnnouncementId],
            });
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
        },
    });

    const cancelMut = useMutation({
        mutationFn: () => cancelAnnouncement(normalizedAnnouncementId!),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["announcement-details", normalizedAnnouncementId],
            });
            queryClient.invalidateQueries({ queryKey: ["announcements"] });
        },
    });

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (!normalizedAnnouncementId || error || !ann) {
        return (
            <div className="space-y-4">
                <Link
                    to="/notifications/announcements"
                    className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
                >
                    <ArrowLeftIcon className="h-4 w-4" /> Back to Announcements
                </Link>
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                    <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                        {!normalizedAnnouncementId
                            ? "This announcement link is missing a valid ID"
                            : "Failed to load announcement details"}
                    </p>
                </div>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    const canSend = ann.status === "DRAFT" || ann.status === "SCHEDULED";
    const canCancel = ann.status === "SCHEDULED";
    const canManageMessaging = hasPermission(SECTION_PERMISSIONS.MESSAGING_MANAGE[0]);
    const canViewApprovals = hasPermission(SECTION_PERMISSIONS.APPROVALS[0]);
    const announcementStatus = ann.status ?? "DRAFT";
    const announcementType = getAnnouncementType(ann) ?? "GENERAL";
    const pushCount = getAnnouncementPushCount(ann);
    const emailCount = getAnnouncementEmailCount(ann);
    const failedCount = getAnnouncementFailedCount(ann);
    const sendSubmission = sendMut.data?.data;

    return (
        <div className="space-y-6">
            {/* Back link */}
            <Link
                to="/notifications/announcements"
                className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:underline dark:text-primary-400"
            >
                <ArrowLeftIcon className="h-4 w-4" /> Back to Announcements
            </Link>

            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
                        <MegaphoneIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                            {ann.title || "Untitled Announcement"}
                        </h2>
                        <div className="mt-1 flex items-center gap-2">
                            <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[announcementStatus] ?? statusColors.DRAFT
                                    }`}
                            >
                                {fmtLabel(announcementStatus)}
                            </span>
                            <span className="text-xs text-slate-400">{fmtLabel(announcementType)}</span>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                    {canManageMessaging && canSend && (
                        <Button
                            size="sm"
                            onClick={() => sendMut.mutate()}
                            disabled={sendMut.isPending}
                        >
                            <PaperAirplaneIcon className="mr-1.5 h-4 w-4" />
                            {sendMut.isPending ? "Submitting…" : "Submit for Approval"}
                        </Button>
                    )}
                    {canManageMessaging && canCancel && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => cancelMut.mutate()}
                            disabled={cancelMut.isPending}
                        >
                            <XCircleIcon className="mr-1.5 h-4 w-4" />
                            {cancelMut.isPending ? "Cancelling…" : "Cancel"}
                        </Button>
                    )}
                </div>
            </div>

            {/* Mutation feedback */}
            {locationFeedback ? (
                <div
                    className={`rounded-xl px-4 py-3 text-sm ${
                        locationFeedback.type === "success"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    {locationFeedback.text}
                </div>
            ) : null}
            {sendMut.isSuccess && (
                <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>{buildApprovalSubmissionFeedback(sendMut.data!, "Announcement submitted for approval.")}</span>
                    {canViewApprovals && sendSubmission?.approvalId ? (
                        <Link
                            to={getApprovalQueuePath(sendSubmission.approvalId)}
                            className="font-semibold underline"
                        >
                            Review queue item
                        </Link>
                    ) : null}
                </div>
            )}
            {sendMut.isError && (
                <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
                    {extractApiError(sendMut.error)}
                </div>
            )}
            {cancelMut.isSuccess && (
                <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                    <CheckCircleIcon className="h-5 w-5" /> Announcement cancelled
                </div>
            )}
            {cancelMut.isError && (
                <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
                    {extractApiError(cancelMut.error)}
                </div>
            )}

            {/* Details Grid */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Message */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 lg:col-span-2">
                    <h3 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Message
                    </h3>
                    <p className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
                        {ann.message}
                    </p>
                </div>

                {/* Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Details
                    </h3>
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Type", fmtLabel(announcementType)],
                            ["Target Audience", fmtLabel(ann.targetAudience)],
                            ["Target Region", ann.targetRegion || "—"],
                            ["Created By", ann.createdBy],
                            ["Created At", fmtDate(ann.createdAt)],
                            ["Sent At", fmtDate(ann.sentAt)],
                            ["Scheduled For", fmtDate(ann.scheduledFor)],
                            ["Expires At", fmtDate(ann.expiresAt)],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Delivery Channels & Stats */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Delivery
                    </h3>

                    {/* Channels */}
                    <div className="mb-4 flex gap-4">
                        {[
                            { label: "Push", active: ann.sendPush },
                            { label: "Email", active: ann.sendEmail },
                        ].map((ch) => (
                            <span
                                key={ch.label}
                                className={`rounded-full px-3 py-1 text-xs font-medium ${ch.active
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                        : "bg-slate-100 text-slate-400 line-through dark:bg-slate-800 dark:text-slate-500"
                                    }`}
                            >
                                {ch.label}
                            </span>
                        ))}
                    </div>

                    {/* Stats */}
                    <dl className="space-y-2 text-sm">
                        {[
                            ["Push Delivered", fmtNumber(pushCount)],
                            ["Email Delivered", fmtNumber(emailCount)],
                            ["Failed Deliveries", fmtNumber(failedCount)],
                        ].map(([label, value]) => (
                            <div key={label} className="flex justify-between">
                                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                                <dd className="font-medium text-slate-900 dark:text-white">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    );
};
